import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { getMPDataWithoutParams } from 'src/mentor/services'
import { LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED } from 'src/consumer/constants'
import { createERMDatabase, deleteERMDatabase } from './indexedDb'
import {
  addOrUpdateAdvicesInIdb,
  addOrUpdateDiagnosisInIdb,
  addOrUpdateDrugAdviceEntitiesInIdb,
  addOrUpdateDrugsInIdb,
  addOrUpdateGenericDrugsInIdb,
  addOrUpdateHealthComplaintsInIdb,
  addOrUpdateInstructionsInIdb,
  addOrUpdateInvestigationInIdb,
  addOrUpdateObservationsInIdb,
  addOrUpdatePatientsInIdb,
  addOrUpdatePsychAssessmentsInIdb,
  clearDecryptedStoreCache,
  DRUG_ADVICE_ENTITY_PARAMS_WITH_IDB_STORE,
} from './indexedDbDao'

dayjs.extend(utc)

// Background population of the EMR IndexedDB.
//
// Safety/resilience properties:
// - single-flight per tab, and a Web Locks guard so two tabs never sync concurrently
// - starts when the browser is idle (never competes with initial page render) and
//   waits for connectivity; yields between pages so the UI stays responsive
// - every page fetch retries with exponential backoff before giving up
// - a store's last-synced timestamp advances ONLY after that store fully synced for
//   every clinic brand — a failed run leaves the timestamp behind, so the next run
//   re-covers the missed window instead of silently losing it
// - one store failing never blocks the others; failures are collected and reported
// - re-runs periodically while the app stays open, so local data keeps tracking the
//   server without any user action

const SYNC_LOCK_NAME = 'erm-idb-background-sync'
const PAGE_SIZE = 500
const MAX_PAGE_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 1500
const INTER_PAGE_DELAY_MS = 50
const PERIODIC_RESYNC_INTERVAL_MS = 30 * 60 * 1000
const STORAGE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

const KEYS = LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED

// progressKey values intentionally match the legacy sync-dialog keys
const SYNC_DEFINITIONS = [
  {
    progressKey: 'drugs',
    storageKey: KEYS.DRUGS,
    documentType: 'drugs',
    listPath: '/api/secure/drug/list',
    writeFn: addOrUpdateDrugsInIdb,
    estimatedPages: 200,
  },
  {
    progressKey: 'complaints',
    storageKey: KEYS.COMPLAINTS,
    documentType: 'health_complaints',
    listPath: '/api/secure/clinic-practitioner/health-complaint/list',
    writeFn: addOrUpdateHealthComplaintsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'diagnosis',
    storageKey: KEYS.DIAGNOSIS,
    documentType: 'diagnosis',
    listPath: '/api/secure/clinic-practitioner/diagnosis/list',
    writeFn: addOrUpdateDiagnosisInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'observations',
    storageKey: KEYS.OBSERVATIONS,
    documentType: 'health_observations',
    listPath: '/api/secure/clinic-practitioner/health-observation/list',
    writeFn: addOrUpdateObservationsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'advices',
    storageKey: KEYS.ADVICES,
    documentType: 'prescription_advice',
    listPath: '/api/secure/clinic-practitioner/prescription-advice/list',
    writeFn: addOrUpdateAdvicesInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'instruction',
    storageKey: KEYS.INSTRUCTIONS,
    documentType: 'instructions',
    listPath: '/api/secure/clinic-practitioner/instruction/list',
    writeFn: addOrUpdateInstructionsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'investigation',
    storageKey: KEYS.INVESTIGATION,
    documentType: 'investigation',
    listPath: '/api/secure/clinic-practitioner/investigation/list',
    writeFn: addOrUpdateInvestigationInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'patients',
    storageKey: KEYS.PATIENTS,
    documentType: 'patients',
    listPath: '/api/secure/patient/list',
    writeFn: addOrUpdatePatientsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'psychAssessments',
    storageKey: KEYS.PSYCH_ASSESSMENTS,
    documentType: 'psych_assessments',
    listPath: '/api/secure/clinic-practitioner/psych-assessment/list',
    writeFn: addOrUpdatePsychAssessmentsInIdb,
    estimatedPages: 50,
  },
  {
    progressKey: 'genericDrugs',
    storageKey: KEYS.GENERIC_DRUGS,
    documentType: 'drugs_generic_names',
    listPath: '/api/secure/clinic-practitioner/drug-generic-formula/list',
    writeFn: addOrUpdateGenericDrugsInIdb,
    estimatedPages: 50,
  },
]

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function waitForBrowserIdle() {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => resolve(), { timeout: 8000 })
    } else {
      setTimeout(resolve, 4000)
    }
  })
}

function waitUntilOnline() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      resolve()
      return
    }
    window.addEventListener('online', () => resolve(), { once: true })
  })
}

async function fetchPageWithRetry(url) {
  let lastError
  for (let attempt = 1; attempt <= MAX_PAGE_ATTEMPTS; attempt++) {
    try {
      await waitUntilOnline()
      const response = await getCPDataWithoutParams(url)
      if (response?.status === 200 && Array.isArray(response?.data)) {
        return response.data
      }
      lastError = new Error(`Unexpected response (status ${response?.status}) for ${url}`)
    } catch (error) {
      lastError = error
    }
    if (attempt < MAX_PAGE_ATTEMPTS) {
      await sleep(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1))
    }
  }
  throw lastError
}

async function syncStoreForBrand({ definition, clinicBrandId, modifiedAfter, onProgress }) {
  let page = 1
  while (true) {
    const modifiedAfterParam = modifiedAfter
      ? `&modifiedAfter=${dayjs.utc(modifiedAfter).format('YYYY-MM-DDTHH:mm:ss')}Z`
      : ''
    const url = `${definition.listPath}?search=&clinicBrandId=${clinicBrandId}&page=${page}&pageSize=${PAGE_SIZE}${modifiedAfterParam}`
    const records = await fetchPageWithRetry(url)
    if (records.length === 0) {
      break
    }
    await definition.writeFn(records)
    onProgress?.(
      `${definition.progressKey}-${clinicBrandId}`,
      Math.min((page * 100) / (definition.estimatedPages || 50), 99),
    )
    page += 1
    // breathe between pages so a long first sync never hogs the event loop
    await sleep(INTER_PAGE_DELAY_MS)
  }
  onProgress?.(`${definition.progressKey}-${clinicBrandId}`, 100)
}

/**
 * Server-side last-update times per document type (max across brands). A failed
 * status call leaves the type unknown, which we treat the same way the legacy sync
 * did: skip when we already have local data, sync when we have none.
 */
async function getMongoLastUpdatedByDocumentType(clinicBrandIds) {
  const byType = {}
  await Promise.all(
    clinicBrandIds.map(async (clinicBrandId) => {
      try {
        const response = await getMPDataWithoutParams(
          `/api/secure/doctor/medical-document-update-status/list?clinicBrandId=${clinicBrandId}`,
        )
        if (response?.status === 200 && Array.isArray(response?.data)) {
          response.data.forEach((item) => {
            const lastUpdateTime = item?.lastUpdateTimeUtc
            if (!item?.documentType || !lastUpdateTime) {
              return
            }
            if (
              !byType[item.documentType] ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(byType[item.documentType]))
            ) {
              byType[item.documentType] = lastUpdateTime
            }
          })
        }
      } catch (error) {
        console.warn('Could not read medical-document-update-status for', clinicBrandId, error)
      }
    }),
  )
  return byType
}

async function runEmrSync({ clinicBrandIds, force, onProgress }) {
  if (force) {
    await deleteERMDatabase()
    clearDecryptedStoreCache()
  }
  // opening the DB first matters: a pending version upgrade wipes the stores and
  // clears the last-updated keys, and that must happen BEFORE we read those keys
  await createERMDatabase()

  const mongoLastByType = force ? {} : await getMongoLastUpdatedByDocumentType(clinicBrandIds)
  const failures = []
  const synced = []
  const skipped = []

  for (const definition of SYNC_DEFINITIONS) {
    const lastSyncedLocal = force ? null : localStorage.getItem(definition.storageKey)
    let modifiedAfter = null
    if (lastSyncedLocal) {
      const mongoLast = mongoLastByType[definition.documentType]
      if (!mongoLast || !dayjs.utc(mongoLast).isAfter(dayjs.utc(lastSyncedLocal))) {
        skipped.push(definition.progressKey)
        continue
      }
      modifiedAfter = lastSyncedLocal
    }

    // captured before fetching so writes that land mid-sync are re-covered next time
    const startedAtUtc = dayjs.utc().format(STORAGE_TIME_FORMAT)
    try {
      for (const clinicBrandId of clinicBrandIds) {
        await syncStoreForBrand({ definition, clinicBrandId, modifiedAfter, onProgress })
      }
      localStorage.setItem(definition.storageKey, startedAtUtc)
      synced.push(definition.progressKey)
    } catch (error) {
      // timestamp deliberately NOT advanced — the next run re-syncs this window
      console.warn(`EMR background sync failed for ${definition.progressKey}`, error)
      failures.push({ store: definition.progressKey, error: error?.message })
    }
  }

  // Rx dropdown value stores (dosage/duration/frequency/timing/type/notes) — small
  // lists, synced incrementally on every run under one shared timestamp
  const adviceLastSynced = force ? null : localStorage.getItem(KEYS.DRUG_ADVICE_ENTITIES)
  const adviceStartedAtUtc = dayjs.utc().format(STORAGE_TIME_FORMAT)
  try {
    for (const clinicBrandId of clinicBrandIds) {
      for (const entityParam of DRUG_ADVICE_ENTITY_PARAMS_WITH_IDB_STORE) {
        await syncStoreForBrand({
          definition: {
            progressKey: entityParam,
            listPath: `/api/secure/clinic-practitioner/${entityParam}/list`,
            writeFn: (items) => addOrUpdateDrugAdviceEntitiesInIdb(entityParam, items),
            estimatedPages: 2,
          },
          clinicBrandId,
          modifiedAfter: adviceLastSynced,
          onProgress,
        })
      }
    }
    localStorage.setItem(KEYS.DRUG_ADVICE_ENTITIES, adviceStartedAtUtc)
    synced.push('drugAdviceEntities')
  } catch (error) {
    console.warn('EMR background sync failed for drug advice entities', error)
    failures.push({ store: 'drugAdviceEntities', error: error?.message })
  }

  return { synced, skipped, failures }
}

let inFlightSync = null

/**
 * Kicks off one background sync run. Safe to call from anywhere, any number of
 * times: concurrent calls in the same tab join the in-flight run, and the Web Locks
 * guard makes a second tab skip instead of double-syncing.
 */
export function startEmrBackgroundSync({
  clinicBrandIds,
  force = false,
  waitForIdleFirst = true,
  onProgress,
} = {}) {
  const brands = [...new Set((clinicBrandIds || []).filter(Boolean))]
  if (brands.length === 0) {
    return Promise.resolve({ skippedRun: 'no-clinic-brands' })
  }
  if (inFlightSync) {
    return inFlightSync
  }
  inFlightSync = (async () => {
    try {
      if (waitForIdleFirst) {
        await waitForBrowserIdle()
      }
      await waitUntilOnline()

      const runner = () => runEmrSync({ clinicBrandIds: brands, force, onProgress })
      if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return await navigator.locks.request(SYNC_LOCK_NAME, { ifAvailable: true }, async (lock) => {
          if (!lock) {
            return { skippedRun: 'another-tab-is-syncing' }
          }
          return await runner()
        })
      }
      return await runner()
    } catch (error) {
      console.warn('EMR background sync failed; it will retry on the next run', error)
      return { failedRun: true, error: error?.message }
    } finally {
      inFlightSync = null
    }
  })()
  return inFlightSync
}

let periodicResyncHandle = null
let periodicBrandIds = []

/**
 * Starts an immediate background sync and keeps re-syncing on an interval while the
 * app stays open, so the local data tracks the server without user action. Repeat
 * calls just refresh the brand list the interval works with.
 */
export function ensurePeriodicEmrBackgroundSync({ clinicBrandIds } = {}) {
  periodicBrandIds = [...new Set((clinicBrandIds || []).filter(Boolean))]
  startEmrBackgroundSync({ clinicBrandIds: periodicBrandIds })
  if (periodicResyncHandle) {
    return
  }
  periodicResyncHandle = setInterval(() => {
    startEmrBackgroundSync({ clinicBrandIds: periodicBrandIds, waitForIdleFirst: false })
  }, PERIODIC_RESYNC_INTERVAL_MS)
}
